/**
 * rendering-registry/types.ts
 *
 * All types for the TeachBud Rendering Registry.
 *
 * VERSIONING
 * ─────────────────────────────────────────────
 * REGISTRY_VERSION follows semver:
 *   MAJOR — breaking change to universal rules, pagination, or blueprint structure
 *   MINOR — new representation added (additive, safe)
 *   PATCH — parameter tweak, default adjustment, fallback change
 *
 * RISK TIERS (from docs/adding-a-representation.md)
 *   Low    — new entry for a concept with no existing rule (additive)
 *   Medium — changing a default for one grade band or subject
 *   High   — touching universal typography, pagination, or blueprint structure
 */

// ── Primitive enumerations ─────────────────────────────────────────────────────

export type WorkspaceSize = 'none' | 'small' | 'medium' | 'large' | 'xl';

/**
 * The workspace kind rendered below a student problem on the activity sheet.
 * When adding a new representation, add its kind here first.
 */
export type WorkspaceKind =
  | 'none'
  | 'lines'
  | 'computation'
  | 'diagram'
  | 'ten-frame'
  | 'number-bond'
  | 'fraction-strip'
  | 'number-line'
  | 'sequence-chain'
  | 'venn-diagram'
  | 'cer-organizer'
  | 't-chart'
  | 'story-map'
  | 'main-idea-web'
  | 'sound-boxes'
  | 'picture-letter';

/**
 * The abstract representation type — one per visual component family.
 * WorkspaceKind and RepresentationType are deliberately separate:
 *   - WorkspaceKind governs the activity sheet workspace below a problem.
 *   - RepresentationType governs which SVG component renders (activity sheet,
 *     presentation slide, assessment workspace).
 */
export type RepresentationType =
  | 'ten-frame'
  | 'number-bond'
  | 'flow-diagram'
  | 'diagram'
  | 'computation'
  | 'lines'
  | 'fraction-strip'
  | 'number-line'
  | 'sequence-chain'
  | 'venn-diagram'
  | 'cer-organizer'
  | 't-chart'
  | 'story-map'
  | 'main-idea-web'
  | 'sound-boxes'
  | 'picture-letter';

/** Axis 1 of the Instructional Intent pipeline — the cognitive operation. */
export type CognitiveOp =
  | 'identify'
  | 'compare'
  | 'classify'
  | 'model'
  | 'sequence'
  | 'measure'
  | 'explain'
  | 'construct'
  | 'analyze'
  | 'justify';

/** Axis 2 of the Instructional Intent pipeline — the concept domain. */
export type ConceptDomain =
  | 'mathematical-quantity'
  | 'living-system'
  | 'physical-process'
  | 'earth-system'
  | 'historical-civic'
  | 'literary-element'
  | 'linguistic-unit'
  | 'abstract-idea';

// ── Match criteria ─────────────────────────────────────────────────────────────

/**
 * Match criteria for a registry entry.
 *
 * ALL defined fields must pass (AND logic). Undefined fields match anything.
 * An empty object `{}` is the universal catch-all.
 *
 * For promptPatterns: at least ONE pattern must match the lowercased prompt.
 */
export interface MatchCriteria {
  /** Grade range [min, max] inclusive. 0 = Kindergarten, 1–12 = Grades 1–12. */
  gradeRange?: [number, number];
  /** Subject names that trigger this entry, e.g. ['Math']. */
  subjects?: string[];
  /** Axis 1 cognitive operations from the rendering pipeline. */
  cognitiveOps?: CognitiveOp[];
  /** Axis 2 concept domains from the rendering pipeline. */
  conceptDomains?: ConceptDomain[];
  /** Standard code prefixes, e.g. ['K.CC', '1.OA']. Narrows to exact standards. */
  standardPrefixes?: string[];
  /**
   * Regex patterns tested against the lowercased problem prompt.
   * At least one must match if this field is defined.
   */
  promptPatterns?: string[];
}

// ── Fallback chain ─────────────────────────────────────────────────────────────

/**
 * What to do when a representation's component fails.
 *
 * CRITICAL CONSTRAINT (from reviewer): Fallback chains must remain
 * instructionally coherent. A visual representation must never silently
 * degrade to a generic computation or text box. Options:
 *
 *   - { kind: 'entry' }        — try a different representation (must be instructionally appropriate)
 *   - { kind: 'terminal' }     — a terminal workspace that always succeeds
 *   - { kind: 'block-export' } — fail loudly with a named error rather than silently degrade
 *
 * Current usage:
 *   Visual representations fall back to 'diagram' (blank visual drawing space) or
 *   terminal 'lines' as the ultimate safety net.
 *   The 'block-export' kind is reserved for future concept-specific SVGs (food-web,
 *   fraction-strip) where degrading to a blank box is unacceptable.
 */
export type FallbackBehavior =
  | { kind: 'entry'; id: string }                                // follow the chain to another entry
  | { kind: 'terminal'; workspaceKind: WorkspaceKind } // always renders — chain ends here
  | {
      kind: 'block-export';
      message: string; // shown to teacher: "Required visual [name] could not be rendered."
    };

// ── Registry entry ─────────────────────────────────────────────────────────────

export interface RegistryEntry {
  /** Unique kebab-case identifier. Never rename or reuse. Referenced by fallback chains. */
  id: string;
  /** Which spec version introduced this entry. e.g. "3.1.0" */
  specVersion: string;
  /** Human-readable description used in error messages and documentation. */
  description: string;

  /**
   * Match criteria — all defined fields must pass.
   * More specific criteria earn higher scores in the resolver.
   */
  match: MatchCriteria;

  /** Representation identity and imageHint routing. */
  representation: {
    type: RepresentationType;
    /**
     * Regex pattern (case-insensitive) that identifies imageHints belonging to
     * this representation. Used by the presentation slide router.
     */
    imageHintPattern?: string;
    /**
     * Example imageHint strings for injection into AI generation prompts.
     * MUST use exactly these formats — the imageHintPattern must recognize them.
     */
    imageHintExamples?: string[];
  };

  /** Activity sheet workspace configuration. */
  activitySheet: {
    workspaceKind: WorkspaceKind;
    /** Pixel heights per workspace size. 0 disables the workspace for that size. */
    wsHeights: Record<WorkspaceSize, number>;
  };

  /** Presentation slide configuration (optional if representation has no slide visual). */
  presentation?: {
    /** Example imageHint for the AI slide generation prompt. */
    imageHintExample?: string;
  };

  /** Assessment configuration. */
  assessment?: {
    allowed: boolean;
    workspaceKind?: WorkspaceKind;
  };

  /**
   * Fallback behavior if this entry's component fails.
   * Must stay instructionally coherent — visual ↛ computation.
   */
  fallback: FallbackBehavior;

  /** Which props are teacher-editable in the format panel. */
  editableProps?: string[];

  /** Example prompts for documentation and testing. */
  examples?: string[];
}

// ── Resolution ─────────────────────────────────────────────────────────────────

/** Input to the resolver — all fields optional except `prompt`. */
export interface ResolutionContext {
  /** 0 = Kindergarten, 1–12 = Grades 1–12. */
  grade?: number;
  subject?: string;
  /** The problem prompt text (required — the primary dispatch signal). */
  prompt: string;
  cognitiveOp?: CognitiveOp;
  conceptDomain?: ConceptDomain;
  standardPrefix?: string;
}

/** Successful resolution — a representation was found. */
export interface ResolvedRepresentation {
  blocked: false;
  /** The registry entry that was used. */
  entryId: string;
  representationType: RepresentationType;
  workspaceKind: WorkspaceKind;
  wsHeights: Record<WorkspaceSize, number>;
  /**
   * null  — primary entry matched, no fallback needed.
   * string — the entry id of the fallback that was actually used.
   */
  fallbackUsed: string | null;
}

/**
 * Blocked resolution — the fallback chain reached a block-export node.
 * Generators must show an error and prevent export.
 */
export interface BlockedResolution {
  blocked: true;
  /** The entry that triggered the block. */
  entryId: string;
  message: string;
}

export type Resolution = ResolvedRepresentation | BlockedResolution;

// ── Errors ─────────────────────────────────────────────────────────────────────

/**
 * Thrown by validateRegistry() when two entries claim the same match criteria.
 * Failing loudly at module load prevents silent shadowing of rules.
 */
export class RegistryConflictError extends Error {
  readonly conflictingIds: readonly [string, string];

  constructor(idA: string, idB: string, reason: string) {
    super(`Registry conflict between "${idA}" and "${idB}": ${reason}`);
    this.name = 'RegistryConflictError';
    this.conflictingIds = [idA, idB] as const;
  }
}
